import { Link } from "react-router-dom";

const MEMBER_IMAGES = import.meta.glob<string>(
  "../images/members/*.{jpg,jpeg,png,webp}",
  { eager: true, import: "default" },
);

const MEMBER_PHOTO_BY_BASENAME = new Map<string, string>();
for (const [path, url] of Object.entries(MEMBER_IMAGES)) {
  const file = path.split("/").pop() ?? "";
  const base = file.replace(/\.(jpe?g|png|webp)$/i, "").toLowerCase();
  MEMBER_PHOTO_BY_BASENAME.set(base, url);
}

type TeamMember = {
  name: string;
  role: string;
  /**
   * Filename stem in `src/images/members` when it doesn't match the auto slug
   * from `name` (e.g. `dr-zulfi` for Dr-Zulfi.jpg).
   */
  photoBase?: string;
};

function nameSlug(name: string): string {
  return name
    .replace(/^dr\.\s*/i, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function memberPhotoUrl(m: Pick<TeamMember, "name" | "photoBase">): string | undefined {
  const slug = nameSlug(m.name);
  const segments = slug.split("-").filter(Boolean);
  const first = segments[0];
  const last =
    segments.length > 1 ? segments[segments.length - 1] : undefined;

  const keys = [
    m.photoBase
      ?.toLowerCase()
      .replace(/\.(jpe?g|png|webp)$/i, ""),
    slug,
    first,
    last && last !== first ? last : undefined,
  ].filter((k): k is string => Boolean(k));

  for (const k of keys) {
    const url = MEMBER_PHOTO_BY_BASENAME.get(k);
    if (url) return url;
  }
  return undefined;
}

function teamInitials(name: string): string {
  const cleaned = name.replace(/^dr\.\s*/i, "").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function TeamMemberCard(member: TeamMember) {
  const { name, role } = member;
  const photoSrc = memberPhotoUrl(member);

  return (
    <div className="text-center">
      <div className="relative mx-auto aspect-square w-full max-w-[11rem] overflow-hidden rounded-lg border border-brand-blue/15 bg-brand-gray/60 shadow-sm">
        {photoSrc ? (
          <img
            src={photoSrc}
            alt={`${name}, ${role}`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-gray/90 to-brand-blue/[0.06]"
            aria-hidden
          >
            <span className="font-display text-2xl font-bold text-brand-maroon/35">
              {teamInitials(name)}
            </span>
          </div>
        )}
      </div>
      <p className="mt-3 font-display text-[11px] font-bold uppercase leading-tight tracking-wide text-brand-maroon sm:text-xs md:text-sm">
        {name}
      </p>
      <p className="mt-1.5 text-[11px] text-brand-blue/80 leading-snug sm:text-xs">
        {role}
      </p>
    </div>
  );
}

const TEAM_EXECUTIVE_PRIMARY: TeamMember[] = [
  { name: "Rizmy Razik", role: "General Secretary", photoBase: "rizmy" },
  {
    name: "Dr. Zulfi Marikkar",
    role: "Vice President",
    photoBase: "dr-zulfi",
  },
];

const TEAM_EXECUTIVE_ADMIN: TeamMember[] = [
  { name: "Faizar A Kuddoos", role: "Admin Secretary", photoBase: "faizar" },
  { name: "Mulaffar Faus", role: "Admin Coordinator", photoBase: "mulaffar" },
];

const TEAM_DIVISION_COLUMNS: { division: string; members: TeamMember[] }[] = [
  {
    division: "Finance",
    members: [
      { name: "Shiran Nizar", role: "Secretary Finance", photoBase: "shiran" },
      {
        name: "Sadhir Nisthar",
        role: "Assistant Secretary Finance",
        photoBase: "sadhir",
      },
      {
        name: "Rizwan Mohamed",
        role: "Coordinator Finance",
        photoBase: "rizwan-mohamed",
      },
    ],
  },
  {
    division: "Education",
    members: [
      {
        name: "Rizwan Haleem",
        role: "Assistant Secretary Education",
        photoBase: "rizwan",
      },
      {
        name: "Safwan Sufiyan",
        role: "Coordinator Education",
        photoBase: "safwan",
      },
      { name: "Ajward Akram", role: "Coordinator Education", photoBase: "ajward" },
    ],
  },
  {
    division: "Sports",
    members: [
      {
        name: "Mufees Muinudeen",
        role: "Assistant Secretary Sports",
        photoBase: "mufees",
      },
      { name: "Mohamed Fasmil", role: "Coordinator Sports", photoBase: "fasmil" },
      { name: "Hasan Nawas", role: "Coordinator Sports", photoBase: "hasan" },
    ],
  },
  {
    division: "Membership",
    members: [
      {
        name: "Raashid Mansoor",
        role: "Assistant Secretary Membership",
        photoBase: "raashid",
      },
      { name: "Rajab Khan", role: "Coordinator Membership", photoBase: "rajab" },
      {
        name: "Rishad Udayar",
        role: "Coordinator Membership",
        photoBase: "rishad",
      },
    ],
  },
  {
    division: "Physical Resources",
    members: [
      {
        name: "Shifan Mohamed",
        role: "Assistant Secretary Physical Resources",
        photoBase: "shifan",
      },
      {
        name: "Nafeel Mohamed",
        role: "Coordinator Physical Resources",
        photoBase: "nafeel",
      },
      {
        name: "Waseem Sufiyan",
        role: "Coordinator Physical Resources",
        photoBase: "waseem",
      },
    ],
  },
  {
    division: "Public Relations",
    members: [
      {
        name: "Mohamed Rafi",
        role: "Assistant Secretary Public Relations",
        photoBase: "rafi",
      },
      {
        name: "Arshad Fawmy",
        role: "Coordinator Public Relations",
        photoBase: "arshad",
      },
      {
        name: "Razeen Rahmathulla",
        role: "Coordinator Public Relations",
        photoBase: "razeen",
      },
    ],
  },
];

const DIVISIONS = [
  {
    title: "Finance",
    description:
      "Managing the association's financial health through budgeting, fund allocation, and fiscal transparency — ensuring every initiative is well-resourced and accountable to the community we serve.",
  },
  {
    title: "Education",
    description:
      "Championing academic excellence and mentorship programmes for current students, bridging the expertise of alumni with the aspirations of future generations of Zahirians.",
  },
  {
    title: "Sports",
    description:
      "Driving the sporting culture of Zahira — organising events, supporting athletes, and building the competitive spirit that represents our school on every field and court.",
  },
  {
    title: "Membership",
    description:
      "Growing and nurturing the OBA community — welcoming new members, strengthening alumni bonds, and ensuring every old boy feels a proud part of the Zahira family.",
  },
  {
    title: "Physical Resources",
    description:
      "Overseeing the upkeep, development, and modernisation of school infrastructure and facilities — ensuring Mother Zahira's physical environment reflects her proud standing.",
  },
  {
    title: "Public Relations",
    description:
      "Shaping the voice and visibility of the OBA — managing communications, events, media, and community outreach to proudly represent Zahira to the wider world.",
  },
] as const;

export function AboutObaPage() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-maroon">
        Zahira College Mawanella
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold text-brand-blue md:text-5xl">
        About the Old Boys&apos; Association
      </h1>
      <p className="mt-2 text-sm font-medium text-brand-blue/70">
        Executive committee term 2025–2028
      </p>

      <div className="mt-10 space-y-6 text-brand-blue/90 leading-relaxed">
        <p>
          The Old Boys&apos; Association of Zahira College Mawanella is a
          structured committee of dedicated alumni, united by a shared
          commitment to serve Mother Zahira with professionalism, clarity, and
          purpose. For the term 2025–2028, the executive committee comprises 20
          members organised across six focused divisions, each led by
          individuals best suited to their area of expertise — ensuring smarter
          decisions, efficient support, and stronger outcomes for our school.
        </p>
      </div>

      <section className="mt-14">
        <h2 className="font-display text-2xl font-bold text-brand-blue md:text-3xl">
          Our six divisions
        </h2>
        <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DIVISIONS.map((division) => (
            <li
              key={division.title}
              className="rounded-2xl border border-brand-blue/10 bg-white p-6 shadow-sm"
            >
              <h3 className="font-display text-lg font-bold text-brand-maroon">
                {division.title}
              </h3>
              <p className="mt-3 text-sm text-brand-blue/90 leading-relaxed">
                {division.description}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section
        className="mt-16 rounded-2xl border border-brand-blue/10 bg-gradient-to-b from-white to-brand-gray/30 p-8 shadow-sm md:p-10"
        aria-labelledby="meet-our-team-heading"
      >
        <h2
          id="meet-our-team-heading"
          className="font-display text-3xl font-bold text-brand-maroon md:text-4xl"
        >
          Meet our team
        </h2>
        <p className="mt-2 text-sm font-semibold text-brand-maroon/85">
          For the year 2025 – 2028
        </p>
        <p className="mt-6 max-w-2xl text-brand-blue/90 leading-relaxed">
          Our team is structured with well-defined expertise areas, ensuring
          smarter decisions, efficient support, and stronger outcomes for{" "}
          <span className="font-semibold text-brand-blue">Mother Zahira</span>.
        </p>

        <div className="mt-12 space-y-10">
          <div>
            <p className="sr-only">Executive leadership</p>
            <ul className="mx-auto flex max-w-xl flex-wrap justify-center gap-8 sm:max-w-none sm:gap-12 md:gap-16">
              {TEAM_EXECUTIVE_PRIMARY.map((m) => (
                <li key={m.name} className="w-[10.5rem] shrink-0 sm:w-44">
                  <TeamMemberCard {...m} />
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="sr-only">Administration</p>
            <ul className="mx-auto flex max-w-xl flex-wrap justify-center gap-8 sm:max-w-none sm:gap-12 md:gap-16">
              {TEAM_EXECUTIVE_ADMIN.map((m) => (
                <li key={m.name} className="w-[10.5rem] shrink-0 sm:w-44">
                  <TeamMemberCard {...m} />
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {TEAM_DIVISION_COLUMNS.map(({ division, members }) => (
            <div key={division}>
              <h3 className="mb-5 border-b border-brand-maroon/25 pb-2 text-center font-display text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">
                {division}
              </h3>
              <ul className="flex flex-col gap-8">
                {members.map((m) => (
                  <li key={`${division}-${m.name}`}>
                    <TeamMemberCard {...m} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <figure className="mt-14 rounded-2xl border-l-4 border-brand-maroon bg-brand-gray/50 p-8 md:p-10">
        <blockquote className="font-display text-lg italic text-brand-blue md:text-xl leading-relaxed">
          &ldquo;It is with great honour that I introduce this committee. Our
          structure is designed to bring more professionalism, faster
          decision-making, and a higher level of support to our Mother
          Zahira.&rdquo;
        </blockquote>
        <figcaption className="mt-6 text-sm text-brand-blue/80 not-italic">
          <span className="font-semibold text-brand-blue">Rizmy Razik</span>
          <span className="block mt-1">
            General Secretary, OBA Zahira College Mawanella
          </span>
        </figcaption>
      </figure>

      <div className="mt-12 rounded-2xl bg-brand-gray/60 p-8 text-center">
        <p className="font-semibold text-brand-blue">Connect with the OBA</p>
        <p className="mt-2 text-sm text-brand-blue/80">
          Reach out for membership, branch information, or general enquiries.
        </p>
        <Link
          to="/contact"
          className="mt-6 inline-flex rounded-full bg-brand-maroon px-8 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-brand-blue"
        >
          Contact us
        </Link>
      </div>
    </div>
  );
}
